import type { ErrorComponentProps } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";
import { Tracking } from "@/components/site/Tracking";
import { CookieBanner } from "@/components/site/CookieBanner";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página não encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A página que procura não existe ou foi movida.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Voltar ao início
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Esta página não carregou
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Ocorreu um erro. Pode tentar novamente ou voltar ao início.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Tentar novamente
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Início
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "InvestCapital — Compramos o seu imóvel a pronto pagamento" },
      { name: "description", content: "InvestCapital compra imóveis a pronto pagamento em Portugal continental, com capitais próprios e proposta em 48h úteis." },
      { name: "author", content: "InvestCapital" },
      { property: "og:site_name", content: "InvestCapital" },
      { property: "og:title", content: "InvestCapital — Compramos o seu imóvel a pronto pagamento" },
      { property: "og:description", content: "Compra de imóveis a pronto pagamento em Portugal continental, com capitais próprios e proposta em 48h úteis." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "InvestCapital — Compramos o seu imóvel a pronto pagamento" },
      { name: "twitter:description", content: "Compra de imóveis a pronto pagamento em Portugal continental, com capitais próprios e proposta em 48h úteis." },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon-investcapital-20260729.png?v=9", type: "image/png" },
      { rel: "shortcut icon", href: "/favicon.ico?v=9", type: "image/x-icon" },
      { rel: "apple-touch-icon", href: "/favicon-investcapital-20260729.png?v=9" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;600&family=Poppins:wght@600;700;900&display=swap",
        media: "print",
        id: "gfonts",
      },
    ],
    scripts: [
      {
        // Google Consent Mode: tudo negado por defeito; a biblioteca só carrega após consentimento.
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',wait_for_update:500});gtag('js',new Date());",
      },
      {
        children:
          "(function(){var l=document.getElementById('gfonts');if(l){if(l.sheet){l.media='all';}else{l.addEventListener('load',function(){l.media='all';});}}})();",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://www.investcapital.pt/#organization",
              name: "InvestCapital",
              url: "https://www.investcapital.pt",
              description:
                "Empresa de gestão e investimento imobiliário que compra imóveis a pronto pagamento com capitais próprios em Portugal continental.",
              email: "proposta@investcapital.pt",
              telephone: "+351934371241",
              areaServed: { "@type": "Country", name: "Portugal" },
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "sales",
                  telephone: "+351934371241",
                  email: "proposta@investcapital.pt",
                  availableLanguage: ["Portuguese"],
                },
              ],
            },
            {
              "@type": "WebSite",
              "@id": "https://www.investcapital.pt/#website",
              url: "https://www.investcapital.pt",
              name: "InvestCapital",
              inLanguage: "pt-PT",
              publisher: { "@id": "https://www.investcapital.pt/#organization" },
            },
            {
              "@type": "Service",
              "@id": "https://www.investcapital.pt/#service",
              name: "Compra de imóveis a pronto pagamento",
              serviceType: "Compra direta de imóveis com capitais próprios",
              provider: { "@id": "https://www.investcapital.pt/#organization" },
              areaServed: { "@type": "Place", name: "Portugal continental" },
              description:
                "Compramos heranças, apartamentos, moradias, prédios, lojas, terrenos e lotes em qualquer estado de conservação, com proposta em 48h úteis, sem intermediários e sem comissões.",
            },
          ],
        }),
      },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Toaster richColors position="top-center" />
      <Tracking />
      <CookieBanner />
      <WhatsAppButton />
    </QueryClientProvider>
  );
}
